import { Fragment, useMemo, type ReactNode } from 'react';
import {
  tableFeatures,
  useTable,
  type DisplayColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';

export type Align = 'left' | 'right' | 'center';
export type RowTone = 'default' | 'selected' | 'flagged' | 'muted';

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

export interface ColumnDef<T> {
  id: string;
  header: ReactNode;
  /** Hides the header text visually but keeps it for screen readers (gutter columns). */
  headerSrOnly?: boolean;
  /** Tailwind width class (w-24, w-40, ...). Exactly ONE column should be 'flex'. */
  width: string | 'flex';
  /** Tailwind min-w-* class. Only meaningful on the flex column — guarantees the table overflows instead of crushing. */
  minWidth?: string;
  align?: Align;
  /** Right-aligns and applies the numeric type role (tabular figures, medium weight). Money and counts. */
  numeric?: boolean;
  sortable?: boolean;
  /** Sort key reported to onSortChange. Defaults to the column id. */
  sortKey?: string;
  /** Column is flush to the card edge — matches the surrounding card's horizontal padding. */
  edge?: boolean;
  /** Cell clicks do not trigger row-level onClick (star toggles, inline actions). */
  stopPropagation?: boolean;
  cell: (row: T) => ReactNode;
  cellClassName?: string;
}

export interface DataTableProps<T extends RowData> {
  /** sr-only <caption> describing the table for screen readers. */
  caption: string;
  rows: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string | number;
  /** Row height is component-owned: default = 44px, compact = 36px. Callers never set it. */
  density?: 'default' | 'compact';
  /** px. Below this the wrapper scrolls horizontally instead of crushing columns. */
  minWidth?: number;
  stickyHeader?: boolean;
  maxHeight?: string;
  sort?: SortState | null;
  onSortChange?: (s: SortState) => void;
  onRowClick?: (row: T) => void;
  /** ONE background per row, resolved in one place. Never concatenated conditionals. */
  rowTone?: (row: T) => RowTone;
  /** Controlled expansion: the rowKey of the open row, or null. */
  expandedKey?: string | number | null;
  /** When provided (with renderExpanded), the leading chevron toggles expansion. */
  onExpandedChange?: (key: string | number | null) => void;
  renderExpanded?: (row: T) => ReactNode;
  loading?: boolean;
  empty?: ReactNode;
  className?: string;
}

const features = tableFeatures({});
type Features = typeof features;

const ROW_H = { compact: 'h-9', default: 'h-11' } as const; // 36 / 44

const TONE: Record<RowTone, string> = {
  default: 'hover:bg-cream-50',
  selected: 'bg-green-50 hover:bg-green-100',
  flagged: 'bg-clay-50 hover:bg-clay-100',
  muted: 'text-ink-faint hover:bg-cream-50',
};

const SKELETON_W = ['w-3/4', 'w-1/2', 'w-2/3'] as const;

function alignCls(c: ColumnDef<never>): string {
  if (c.numeric || c.align === 'right') return 'text-right';
  if (c.align === 'center') return 'text-center';
  return 'text-left';
}

export default function DataTable<T extends RowData>({
  caption,
  rows,
  columns,
  rowKey,
  density = 'default',
  minWidth = 960,
  stickyHeader = true,
  maxHeight = '70vh',
  sort,
  onSortChange,
  onRowClick,
  rowTone,
  expandedKey,
  onExpandedChange,
  renderExpanded,
  loading,
  empty,
  className,
}: DataTableProps<T>) {
  const hasExpander = !!renderExpanded;
  const colSpan = columns.length + (hasExpander ? 1 : 0);

  const colById = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);

  const tsColumns = useMemo(
    () =>
      columns.map(
        (c): DisplayColumnDef<Features, T> => ({
          id: c.id,
          header: () => c.header,
          cell: ({ row }) => c.cell(row.original),
        }),
      ),
    [columns],
  );

  const table = useTable<Features, T>({
    features,
    columns: tsColumns,
    data: rows,
    getRowId: (row) => String(rowKey(row)),
  });

  const headerCellCls = cn(
    'h-9 whitespace-nowrap border-b border-border bg-surface-card px-[var(--pad-cell-x)] align-middle t-eyebrow select-none',
    stickyHeader && 'sticky top-0 z-10',
  );

  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--radius-surface)] border border-border bg-surface-card',
        className,
      )}
    >
      <div className="overflow-auto" style={{ maxHeight }}>
        {/* table-fixed is load-bearing: with layout:auto the flex column grows to
            its content and pushes Amount/Status past the container edge. */}
        <table className="w-full table-fixed border-separate border-spacing-0" style={{ minWidth }}>
          <caption className="sr-only">{caption}</caption>
          <colgroup>
            {hasExpander && <col className="w-10" />}
            {columns.map((c) => (
              <col key={c.id} className={c.width === 'flex' ? 'w-auto' : c.width} />
            ))}
          </colgroup>

          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {hasExpander && (
                  <th scope="col" className={headerCellCls}>
                    <span className="sr-only">Expand row</span>
                  </th>
                )}
                {headerGroup.headers.map((header) => {
                  const c = colById.get(header.column.id);
                  if (!c) return null;
                  const key = c.sortKey ?? c.id;
                  const active = sort?.key === key;
                  const Icon = sort?.dir === 'desc' ? ChevronDown : ChevronUp;
                  const label = (
                    <span className={c.headerSrOnly ? 'sr-only' : undefined}>{c.header}</span>
                  );
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        active
                          ? sort!.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : c.sortable
                            ? 'none'
                            : undefined
                      }
                      className={cn(
                        headerCellCls,
                        alignCls(c as ColumnDef<never>),
                        c.minWidth,
                        c.edge && 'first:pl-4 last:pr-4',
                      )}
                    >
                      {c.sortable && onSortChange ? (
                        <button
                          type="button"
                          onClick={() =>
                            onSortChange({
                              key,
                              dir: active && sort!.dir === 'asc' ? 'desc' : 'asc',
                            })
                          }
                          className={cn(
                            'inline-flex items-center gap-1 rounded-[var(--radius-control)] transition-colors hover:text-ink',
                            (c.numeric || c.align === 'right') && 'flex-row-reverse',
                            active && 'text-ink',
                          )}
                        >
                          {label}
                          {active && <Icon size={12} aria-hidden="true" />}
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className="[&>tr:last-child>td]:border-b-0">
            {loading &&
              Array.from({ length: 8 }, (_, i) => (
                <tr key={i} className={ROW_H[density]}>
                  {hasExpander && <td className="border-b border-border-subtle" />}
                  {columns.map((c, j) => (
                    <td
                      key={c.id}
                      className="border-b border-border-subtle px-[var(--pad-cell-x)] align-middle"
                    >
                      <div
                        className={cn(
                          'skeleton h-3',
                          SKELETON_W[(i + j) % SKELETON_W.length],
                          (c.numeric || c.align === 'right') && 'ml-auto',
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="p-0">
                  {empty ?? (
                    <div className="py-12 text-center text-sm text-ink-faint">No results</div>
                  )}
                </td>
              </tr>
            )}

            {!loading &&
              table.getRowModel().rows.map((row) => {
                const k = rowKey(row.original);
                const expanded = hasExpander && expandedKey != null && String(expandedKey) === row.id;
                const tone: RowTone = expanded ? 'selected' : (rowTone?.(row.original) ?? 'default');
                return (
                  <Fragment key={row.id}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                      className={cn(
                        ROW_H[density],
                        'transition-colors',
                        TONE[tone],
                        onRowClick && 'cursor-pointer',
                      )}
                    >
                      {hasExpander && (
                        <td className="border-b border-border-subtle pl-2 align-middle">
                          <button
                            type="button"
                            aria-expanded={expanded}
                            aria-label={expanded ? 'Collapse row' : 'Expand row'}
                            onClick={
                              onExpandedChange
                                ? (e) => {
                                    e.stopPropagation();
                                    onExpandedChange(expanded ? null : k);
                                  }
                                : undefined
                            }
                            className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-control)] text-ink-faint transition-colors hover:bg-cream-50 hover:text-ink"
                          >
                            <ChevronRight
                              size={16}
                              aria-hidden="true"
                              className={cn(
                                'transition-transform duration-[var(--dur-fast)]',
                                expanded && 'rotate-90',
                              )}
                            />
                          </button>
                        </td>
                      )}
                      {row.getAllCells().map((cell) => {
                        const c = colById.get(cell.column.id);
                        if (!c) return null;
                        return (
                          <td
                            key={cell.id}
                            onClick={c.stopPropagation ? (e) => e.stopPropagation() : undefined}
                            className={cn(
                              'overflow-hidden border-b border-border-subtle px-[var(--pad-cell-x)] align-middle text-base text-ink',
                              alignCls(c as ColumnDef<never>),
                              c.numeric && 't-num',
                              c.minWidth,
                              c.edge && 'first:pl-4 last:pr-4',
                              c.cellClassName,
                            )}
                          >
                            <table.FlexRender cell={cell} />
                          </td>
                        );
                      })}
                    </tr>
                    {expanded && renderExpanded && (
                      <tr>
                        <td
                          colSpan={colSpan}
                          className="border-b border-border-subtle bg-surface-well px-4 py-3"
                        >
                          {renderExpanded(row.original)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { default as Pagination } from './Pagination';
export type { PaginationProps } from './Pagination';
